// PriceFeed Data Manager - Handles real-time price feed data from price-feed-api
// This manager handles live gold and silver price data with subscription management

class PriceFeedDataManager {
  constructor(socket) {
    this.socket = socket;
    this.subscribers = new Map();
    this.data = {
      currentPriceData: {},
      subscriptionStatus: null,
      lastUpdate: null,
    };

    this.setupSocketListeners();
  }

  setupSocketListeners() {
    // Listen to subscription confirmation
    this.socket.on('subscribe', (response) => {
      console.log('PriceFeed subscription confirmed:', response);
      if (response.data && !response.data.error) {
        this.data.subscriptionStatus = 'subscribed';
        this.notifySubscribers('subscriptionStatus', 'subscribed');
      } else {
        console.error('Subscription failed:', response.data?.error);
        this.data.subscriptionStatus = 'failed';
        this.notifySubscribers('subscriptionStatus', 'failed');
      }
    });

    // Listen to real-time price updates
    this.socket.on('current-price', (response) => {
      try {
        if (response.data && typeof response.data === 'object') {
          this.data.currentPriceData = response.data;
          this.data.lastUpdate = new Date();
          this.notifySubscribers('currentPrice', response.data);
        }
      } catch (error) {
        console.error('Error processing current-price data:', error);
      }
    });

    // Listen to connection status
    this.socket.on('connection-status', (response) => {
      console.log('PriceFeed connection status:', response);
      this.notifySubscribers('connectionStatus', response);
    });

    // Listen to errors
    this.socket.on('error', (errorResponse) => {
      console.error('PriceFeed gateway error:', errorResponse);
      this.notifySubscribers(
        'error',
        errorResponse.message || 'Gateway error occurred',
      );
    });

    // Listen to unsubscribe confirmation
    this.socket.on('unsubscribe', (response) => {
      console.log('PriceFeed unsubscribe confirmed:', response);
      this.data.subscriptionStatus = 'unsubscribed';
      this.notifySubscribers('subscriptionStatus', 'unsubscribed');
    });
  }

  // Subscription management
  subscribe(dataType, callback) {
    if (!this.subscribers.has(dataType)) {
      this.subscribers.set(dataType, new Set());
    }
    this.subscribers.get(dataType).add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.get(dataType)?.delete(callback);
    };
  }

  notifySubscribers(dataType, data) {
    this.subscribers.get(dataType)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in PriceFeed subscriber callback:', error);
      }
    });
  }

  // Data access methods
  getCurrentPriceData() {
    return this.data.currentPriceData;
  }

  getSubscriptionStatus() {
    return this.data.subscriptionStatus;
  }

  getLastUpdate() {
    return this.data.lastUpdate;
  }

  // Request methods - communicate with price-feed-api gateway
  subscribeToCurrentPrice(companyCodes = ['CTG']) {
    // Unsubscribe first if already subscribed to prevent multiple subscriptions
    if (this.data.subscriptionStatus === 'subscribed') {
      this.unsubscribeFromCurrentPrice();
      // Small delay to ensure unsubscribe is processed
      setTimeout(() => {
        this.performSubscription(companyCodes);
      }, 100);
    } else {
      this.performSubscription(companyCodes);
    }
  }

  performSubscription(companyCodes = ['CTG']) {
    const subscriptionData = {
      companycode: companyCodes[0] || 'CTG',
    };

    console.log(
      'PriceFeed: Subscribing to current-price updates:',
      subscriptionData,
    );
    this.socket.emit('subscribe', subscriptionData);
  }

  unsubscribeFromCurrentPrice() {
    console.log('PriceFeed: Unsubscribing from current-price updates');
    this.socket.emit('unsubscribe', {});
  }

  // Data transformation methods for different component needs
  getFormattedPriceData() {
    const { currentPriceData } = this.data;

    if (!currentPriceData || Object.keys(currentPriceData).length === 0) {
      return {};
    }

    // Transform the data structure for tomei price override table format
    const formatted = {};

    // Handle different possible data structures from price feed
    if (typeof currentPriceData === 'object') {
      // If it's already in the right format (feed name as key)
      Object.keys(currentPriceData).forEach((feedName) => {
        const feedData = currentPriceData[feedName];
        if (
          feedData &&
          (feedData.WeBuy !== undefined || feedData.WeSell !== undefined)
        ) {
          formatted[feedName] = {
            WeBuy: feedData.WeBuy || feedData.BuyPrice || feedData.FeedBuyPrice,
            WeSell:
              feedData.WeSell || feedData.SellPrice || feedData.FeedSellPrice,
          };
        }
      });
    }

    return formatted;
  }

  // Get summary information
  getPriceSummary() {
    return {
      hasData: Object.keys(this.data.currentPriceData).length > 0,
      subscriptionStatus: this.data.subscriptionStatus,
      lastUpdate: this.data.lastUpdate,
      dataKeys: Object.keys(this.data.currentPriceData),
    };
  }
}

export default PriceFeedDataManager;
